/**
 * Domain and wire types for the host protocol.
 * Constants live in constants.ts; conversion lives in codec.ts.
 */

import type { PatternKind, JigState } from './constants.ts';

export type PatternKindValue = (typeof PatternKind)[keyof typeof PatternKind];
export type JigStateValue = (typeof JigState)[keyof typeof JigState];

/* ------------------------------------------------------------------ device -- */

/** One movement step. 6 bytes on the wire, packed, little-endian. */
export interface JigStep {
  /** -127..127 px */
  dx: number;
  /** -127..127 px */
  dy: number;
  /** -127..127 notches */
  wheel: number;
  /** bit0 = left, bit1 = right, bit2 = middle */
  buttons: number;
  /** dwell after emitting this step, ms */
  delayMs: number;
}

/** The complete device configuration. */
export interface DeviceConfig {
  kind: PatternKindValue;
  flags: number;
  jitter: number;
  seed: number;
  /** 0 means "run until power-off". Milliseconds internally, seconds on the wire. */
  durationMs: number;
  amplitude: number;
  periodMs: number;
  stepCount: number;
  bootDelayMs: number;
  /** True when the live configuration matches what is in flash. */
  saved: boolean;
}

export interface DeviceInfo {
  fwMajor: number;
  fwMinor: number;
  fwPatch: number;
  protoVersion: number;
  caps: number;
  state: JigStateValue;
  saved: boolean;
}

export interface DeviceStatus {
  state: JigStateValue;
  elapsedMs: number;
  /** 0 means unbounded (duration_s == 0). */
  totalMs: number;
  stepIndex: number;
  stepsDone: number;
}

export interface SaveResult {
  ok: boolean;
  /** The HAL status byte when ok is false. */
  halStatus: number;
}

/* ------------------------------------------------------------------- frame -- */

/** The 63-byte MsgFrame, WITHOUT the leading report ID byte. */
export interface Frame {
  msgId: number;
  seq: number;
  totalLen: number;
  /** Length is totalLen; the backing buffer is always MSG_PAYLOAD_SIZE. */
  data: Uint8Array;
}

/* ------------------------------------------------------------------ errors -- */

/** Thrown when the device answers with NACK. Carries the reason byte verbatim. */
export class DeviceNackError extends Error {
  readonly msgId: number;
  readonly reason: number;
  readonly reasonName: string;

  constructor(msgId: number, reason: number, reasonName: string, help?: string) {
    super(`NACK ${reasonName}${help ? ` — ${help}` : ''} (in reply to msgId 0x${msgId.toString(16).padStart(2, '0')})`);
    this.name = 'DeviceNackError';
    this.msgId = msgId;
    this.reason = reason;
    this.reasonName = reasonName;
  }
}

export class DeviceTimeoutError extends Error {
  constructor(what: string, timeoutMs: number) {
    super(`${what} timed out after ${timeoutMs} ms — the device did not answer`);
    this.name = 'DeviceTimeoutError';
  }
}

export class DeviceBusyError extends Error {
  constructor(what: string) {
    super(`${what} was rejected locally: a request is already in flight`);
    this.name = 'DeviceBusyError';
  }
}
